import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuth } from './contexts/AuthContext';
import SpaceLoginPage from './pages/SpaceLoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import LoadingScreen from './components/common/LoadingScreen';

// Import pages
import DashboardPage from './pages/DashboardPage';
import AgentsPage from './pages/AgentsPage';
import SkillsPage from './pages/SkillsPage';
import SkillAssignmentPage from './pages/SkillAssignmentPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ExtensionsPage from './pages/ExtensionsPage';
import DataStructuresPage from './pages/DataStructuresPage';
import CampaignsPage from './pages/CampaignsPage'; // import
import TrunksPage from './pages/TrunksPage';
import AmdPage from './pages/AmdPage';
import DialplansPage from './pages/DialplansPage';
import LoadsPage from './pages/LoadsPage';
import EndsPage from './pages/EndsPage';
import AgentTestPage from './pages/AgentTestPage';
import WorkflowsPage from './pages/WorkflowsPage';
import EngineStatusPage from './pages/EngineStatusPage';
import SimulatorPage from './pages/SimulatorPage';
import SupervisorAgentsPage from './pages/SupervisorAgentsPage';
import SupervisorQueuesPage from './pages/SupervisorQueuesPage';
import SupervisorMonitoringCenterPage from './pages/SupervisorMonitoringCenterPage';
import SupervisorSkillsPage from './pages/SupervisorSkillsPage';
import SkillsMonitorPage from './pages/SkillsMonitorPage';
import CallLogsPage from './pages/CallLogsPage';
import MultimediaPage from './pages/MultimediaPage';
import AgentCallsPage from './pages/AgentCallsPage';
import AgentDailyReportPage from './pages/AgentDailyReportPage';
import ApiTokensPage from './pages/ApiTokensPage';
import WebhooksPage from './pages/WebhooksPage';
import WallPage from './pages/WallPage';
import ChatPage from './pages/ChatPage';
import RolesPage from './pages/RolesPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isLoginRoute = location.pathname === '/login' || location.pathname === '/space-login';

  if (isLoading && !isLoginRoute) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <SpaceLoginPage />
          }
        />
        <Route
          path="/space-login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <SpaceLoginPage />
          }
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Routes>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/extensions" element={<ExtensionsPage />} />
                  <Route path="/trunks" element={<TrunksPage />} />
                  <Route path="/agents" element={<AgentsPage />} />
                  <Route path="/skill-assignment" element={<SkillAssignmentPage />} />
                  <Route path="/dialplans" element={<DialplansPage/>} />
                  <Route path="/skills" element={<SkillsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/agent-daily" element={<AgentDailyReportPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/datastructures" element={<DataStructuresPage />} />
                  <Route path="/campaigns" element={<CampaignsPage />} />
                  <Route path="/amd" element={<AmdPage />} />
                  <Route path="/loads" element={<LoadsPage />} />
                  <Route path="/ends" element={<EndsPage />} />
                  <Route path="/engine-status" element={<EngineStatusPage />} />
                  <Route path="/agent-test" element={<AgentTestPage />} />
                  <Route path="/workflows" element={<WorkflowsPage />} />
                  <Route path="/simulator" element={<SimulatorPage />} />
                  <Route path="/supervision/agents" element={<SupervisorAgentsPage />} />
                  <Route path="/supervision/skills" element={<SupervisorSkillsPage />} />
                  <Route path="/supervision/skills-monitor" element={<SkillsMonitorPage />} />
                  <Route path="/supervision/queues" element={<SupervisorQueuesPage />} />
                  <Route path="/supervision/monitoring-center" element={<SupervisorMonitoringCenterPage />} />
                  <Route path="/supervision/call-logs" element={<CallLogsPage />} />
                  <Route path="/multimedia" element={<MultimediaPage />} />
                  <Route path="/agent-calls" element={<AgentCallsPage />} />
                  <Route path="/wall" element={<WallPage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/api-tokens" element={<ApiTokensPage />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/roles" element={<RolesPage />} />
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                  <Route path="*" element={<Navigate to="/dashboard" />} />

                </Routes>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Box>
  );
}

export default App;
